import type { MeResponse, Portal, WorkspaceSummary } from "@sautria/contracts";
import { useMe } from "./api/session";

export interface WorkspaceChoice {
  workspace: WorkspaceSummary;
  portal: Portal;
}

const key = (userId: string) => `sautria:workspace:${userId}`;

/** Every (company, portal) pair this person can open, in a stable order. */
export function choicesOf(me: MeResponse | null | undefined): WorkspaceChoice[] {
  return (me?.workspaces ?? []).flatMap((workspace) => workspace.portals.map((portal) => ({ workspace, portal })));
}

export function storedChoice(me: MeResponse): WorkspaceChoice | null {
  let raw: string | null = null;
  try {
    raw = localStorage.getItem(key(me.user.id));
  } catch {
    /* storage blocked: no remembered choice */
  }
  const [tenantId, portal] = (raw ?? "").split(":");
  return choicesOf(me).find((c) => c.workspace.tenantId === tenantId && c.portal === portal) ?? null;
}

export function rememberChoice(me: MeResponse, choice: WorkspaceChoice) {
  try {
    localStorage.setItem(key(me.user.id), `${choice.workspace.tenantId}:${choice.portal}`);
  } catch {
    /* the choice lasts for this page only */
  }
}

export const portalPath = (portal: Portal) => (portal === "clinical" ? "/clinical" : "/company");

/** The company open in this portal: the remembered one when it fits, otherwise the first that has the portal. */
export function useActiveWorkspace(portal: Portal) {
  const me = useMe();
  const choices = choicesOf(me.data).filter((c) => c.portal === portal);
  const stored = me.data ? storedChoice(me.data) : null;
  const active = (stored && stored.portal === portal ? stored : choices[0]) ?? null;
  return { active: active?.workspace ?? null, canSwitch: choicesOf(me.data).length > 1 };
}
